import React, { useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Play } from 'lucide-react'
import { subscribeRadioNow } from 'state/radioNowFeed'
import { pauseMediaKey } from 'state/mediaPlaybackCoordinator'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import {
    RadioSongCard,
    RadioSongThreadSurface,
    useMediaDockRadioActive,
    useOpenRadioSongThread,
} from 'components/answers/PostAnswer'
import AudioPlayerCore from 'components/answers/AudioPlayerCore'
import RadioBroadcastTransport from 'components/answers/RadioBroadcastTransport'
import RadioUpvalueButton, { RadioSongValue } from 'components/answers/RadioUpvalueButton'
import { radioSongMedia, radioSongStandIn } from 'components/answers/radioCardMedia'
import { hostedNativeMediaEnabled } from 'lib/hostedMediaPlayback'
import useInlineRadioTuneIn from 'components/shared/MediaDock/useInlineRadioTuneIn'
import styles from 'components/panels/radio-header-card.module.scss'

// The station's latest song in the Music header: the song card the Discover
// tab shows, with no station chrome around it. Its transport is the tuner
// (radio tower, seek line on the station's clock, playlist and upvalue
// buttons bottom right), and it swaps in place when the station advances.
//
// Tuning in plays the station right here, from the station's position, and
// carries on into each next song. Leaving the card hands the song to the
// dock, which takes it as the radio (Radio tab, following the station).
// While the dock has the station, the card is a "Playing in dock"
// placeholder: it plays nothing and doesn't claim the song back.
export default function RadioHeaderCard() {
    const [feed, setFeed] = useState(null)
    const [held, setHeld] = useState(null)
    const cardRef = useRef(null)
    const dockTunedIn = useMediaDockRadioActive()

    const now = feed?.now || null
    const track = now?.track || null
    const songId = track?.answerId ? String(track.answerId) : null
    const nextId = feed?.next?.track?.answerId ? String(feed.next.track.answerId) : null
    const title = track?.title || null
    const song = useCachedAnswer(songId)

    // The next song loads ahead, so the card can swap the moment the station advances.
    useEffect(() => {
        if (songId) queueAnswerHydration(songId, { lane: 'embed' })
        if (nextId) queueAnswerHydration(nextId, { lane: 'embed' })
    }, [songId, nextId])

    // The station's payload already carries the song's media: draw the card
    // from it at once, and let the post (artwork, text) replace it when cached.
    const standIn = useMemo(() => radioSongStandIn(track), [track])
    // Until a new song can be drawn, the previous one stays rather than an empty card.
    const ready = Boolean(songId && (song?.id || standIn))
    const stationOff = Boolean(feed) && !track
    useEffect(() => {
        if (ready) setHeld({ songId, title, standIn })
        else if (stationOff) setHeld(null)
    }, [ready, songId, title, standIn, stationOff])

    const shown = ready ? { songId, title, standIn } : stationOff ? null : held
    const shownSong = useCachedAnswer(shown?.songId || null) || shown?.standIn || null
    const media = radioSongMedia(shownSong)
    // The clock belongs to the play on air; a held song waiting on the next
    // one's load shows no position.
    const clock = shown && shown.songId === songId ? now : null
    const { position, duration } = useStationPosition(clock, feed?.receivedAt)
    const hostedVideo = hostedNativeMediaEnabled()
    const hostedVideoStage = hostedVideo && media?.kind === 'video'
    const stageRef = useRef(null)
    const tuner = useInlineRadioTuneIn({
        cardRef,
        media,
        clock,
        position,
        receivedAt: feed?.receivedAt,
        dockTunedIn,
        source: 'music_header',
        inlineVideo: true,
        // A hosted video song is parked over the card's stage, so the stage is
        // its origin: the dock places its picture there and the card's controls
        // move into the dock's chrome layer above it.
        originRef: hostedVideoStage ? stageRef : cardRef,
    })
    const { coreRef, mediaKey, playsHere, listening } = tuner
    const isVideo = media?.kind === 'video'
    const [hostedPresentation, setHostedPresentation] = useState({
        isHostedHere: false,
        chromeLayer: null,
    })

    // Passive: tuning in counts the listener (useInlineRadioTuneIn).
    // The header shows the song on air, not the odds chart.
    useEffect(
        () => subscribeRadioNow(payload => setFeed(payload || null), { contenders: false }),
        [],
    )

    // The station going quiet while tuned in here: stop the song explicitly (a
    // hosted runtime keeps playing after the card unmounts).
    const lastKeyRef = useRef(null)
    if (mediaKey) lastKeyRef.current = mediaKey
    useEffect(() => {
        if (!stationOff || !listening) return
        if (lastKeyRef.current) pauseMediaKey(lastKeyRef.current)
        tuner.endSession()
    }, [stationOff, listening]) // eslint-disable-line react-hooks/exhaustive-deps

    // First load: a card-sized placeholder until the station's song arrives, so
    // the header doesn't jump. A station with nothing on air shows nothing.
    if (!shown)
        return stationOff ? null : (
            <div className={styles.radioHeaderCard} data-testid="radio-header-loading">
                <div className={styles.loadingCard} role="status" aria-label="Loading the station">
                    <i className="fa fa-circle-o-notch fa-spin fa-lg" aria-hidden="true" />
                </div>
            </div>
        )

    return (
        <div
            ref={cardRef}
            className={styles.radioHeaderCard}
            data-testid="radio-header-card"
            onClick={ev => ev.stopPropagation()}
        >
            {playsHere && !isVideo && (
                <AudioPlayerCore
                    ref={coreRef}
                    originRef={cardRef}
                    mediaKey={mediaKey}
                    src={media.url}
                    className={styles.hiddenAudio}
                    onPlaybackStateChange={tuner.setInlinePlaying}
                />
            )}
            {dockTunedIn && (
                <div
                    className={styles.playingInDock}
                    data-testid="radio-header-playing-in-dock"
                    {...(playsHere
                        ? {
                              role: 'button',
                              tabIndex: 0,
                              'aria-label': 'Play the station here',
                              onClick: tuner.playHere,
                              onKeyDown: event => {
                                  if (event.key !== 'Enter' && event.key !== ' ') return
                                  event.preventDefault()
                                  tuner.playHere(event)
                              },
                          }
                        : {})}
                >
                    <span className={styles.playingInDockLabel}>Playing in dock</span>
                    {playsHere && (
                        <button
                            type="button"
                            className={styles.playHereButton}
                            onClick={tuner.playHere}
                            aria-label="Play the station here"
                        >
                            <Play aria-hidden="true" />
                            Play here
                        </button>
                    )}
                </div>
            )}
            <RadioHeaderSong
                key={shown.songId}
                songId={shown.songId}
                title={shown.title}
                standIn={shown.standIn}
                stage={
                    // A video song plays in the card, its picture over the artwork.
                    // Hosted: the dock's own video is parked over this box, so it
                    // glides to the dock when the card leaves, without a reload.
                    playsHere && isVideo ? (
                        hostedVideo ? (
                            <>
                                <div
                                    ref={stageRef}
                                    className={styles.videoStage}
                                    data-hosted-media-fit="cover"
                                />
                                <AudioPlayerCore
                                    ref={coreRef}
                                    as="video"
                                    originRef={stageRef}
                                    mediaKey={mediaKey}
                                    src={media.url}
                                    onPlaybackStateChange={tuner.setInlinePlaying}
                                    onHostedPresentation={setHostedPresentation}
                                />
                            </>
                        ) : (
                            <AudioPlayerCore
                                ref={coreRef}
                                as="video"
                                originRef={cardRef}
                                mediaKey={mediaKey}
                                src={media.url}
                                className={cn(
                                    styles.inlineVideo,
                                    listening && styles.inlineVideoShown,
                                )}
                                onPlaybackStateChange={tuner.setInlinePlaying}
                            />
                        )
                    ) : undefined
                }
                chromeLayer={
                    hostedVideo && isVideo && hostedPresentation.isHostedHere
                        ? hostedPresentation.chromeLayer
                        : null
                }
                transport={
                    <RadioBroadcastTransport
                        isLive
                        tunedIn={tuner.tunedIn}
                        position={position}
                        duration={duration}
                        progress={duration > 0 && position != null ? position / duration : null}
                        onTuneIn={tuner.tuneIn}
                        onTuneOut={tuner.tuneOut}
                        mediaKey={mediaKey || undefined}
                        answerId={shown.songId}
                        actions={<RadioUpvalueButton songId={shown.songId} />}
                        leading={<RadioSongValue songId={shown.songId} />}
                    />
                }
            />
        </div>
    )
}

function RadioHeaderSong({ songId, title, standIn, transport, stage, chromeLayer }) {
    const openSongThread = useOpenRadioSongThread(null, songId, 'music_header')
    return (
        <RadioSongThreadSurface openSongThread={openSongThread} testId="radio-header-song">
            <RadioSongCard
                // The song card reads the cached post, else this embed: the
                // station's stand-in until the post loads.
                answer={standIn ? { embeds: [standIn] } : null}
                songId={songId}
                title={title}
                showByline={false}
                transport={transport}
                stage={stage}
                chromeLayer={chromeLayer}
            />
        </RadioSongThreadSurface>
    )
}

// The station's position in the play, ticking from the feed's last sample.
function useStationPosition(clock, receivedAt) {
    const [, setTick] = useState(0)
    const duration = Number(clock?.duration) || 0
    const live = Boolean(clock) && Number.isFinite(Number(receivedAt))

    useEffect(() => {
        if (!live) return undefined
        const timer = setInterval(() => setTick(tick => tick + 1), 1000)
        return () => clearInterval(timer)
    }, [live])

    if (!live) return { position: null, duration: duration || null }
    const elapsed = (Date.now() - Number(receivedAt)) / 1000
    const position = Math.max(0, Number(clock.position) || 0) + Math.max(0, elapsed)
    return {
        position: duration > 0 ? Math.min(position, duration) : position,
        duration: duration || null,
    }
}
